/**
 * StreamPanel — always-rendered top panel of the creator-hub section.
 *
 * Persistent across tab switches in the parent — switching CHALLENGES/CLIPS/
 * SOCIAL below does NOT unmount this panel.
 *
 * Playback is INLINE in this panel (no modal, no leaving the page). Click the
 * poster → the same area swaps to the inline player and starts playing:
 *   · Twitch live + VOD     → Twitch iframe
 *   · YouTube live + VOD    → YouTube iframe
 *   · Kick LIVE             → Kick iframe
 *   · Kick VOD              → native <video> + hls.js (Kick has no VOD iframe)
 *
 * Source toggle lets the viewer switch platforms; selecting a different
 * source resets to the poster (one play action per source change).
 */

import React, { useMemo, useState } from "react";
import { FaCircle } from "react-icons/fa";
import type { StreamingSettings, FeaturedStreamResponse } from "@/hooks/hub/useGetHub";


import { useGetDropLiveStatus } from "@/hooks/drop/useGetDropLiveStatus";
import { useHeroFallback } from "./stream-panel/useHeroFallback";
import { useFullscreenRow } from "./stream-panel/useFullscreenRow";
import { buildInlineSource } from "./stream-panel/buildInlineSource";
import { type InlinePlatform } from "./StreamInlinePlayer";
import { StreamPanelColumn } from "./stream-panel/StreamPanelColumn";
import { ChatPanel } from "./ChatPanel";
import { ActiveChallengePanel } from "./ActiveChallengePanel";
import { getStreamExternalUrl, platformLabel, asStreamingSource, type StreamingSource, type SelectedClip } from "./shared";

interface StreamPanelProps {
    surfaceId?: string;
    /* Drop slug — set for DropPage consumers; takes precedence over surfaceId for content fetches */
    dropSlug?: string;
    hubName?: string;
    streamingSettings?: StreamingSettings;
    /*
     * When set AND no source is live, this clip takes over the main player.
     * Live streams always win — clicking a clip while live is a no-op visually
     * because the live stream remains the playback target.
     */
    selectedClip?: SelectedClip | null;
    onClearSelectedClip?: () => void;
    /*
     * The resolved active platform (NOT the raw user-selection state).
     * CreatorSection computes activePlatform from the user's stored choice
     * with fallback to first-live → first-configured, so the icons row and
     * this player can't disagree mid-render. Passing the raw `activeSource`
     * state instead would force this component to re-derive — and any
     * divergence between the two derivations would be a UX bug.
     */
    activePlatform?: StreamingSource | null;
    /*
     * Raw state setter — used only by handleReturnToLive below to switch the
     * player back to the live platform after a clip ends. Bypasses the
     * resolved-value computation in CreatorSection because we explicitly want
     * to overwrite the stored preference. localStorage persistence is handled
     * by a CreatorSection useEffect that watches activeSource changes.
     */
    setActiveSource?: (platform: StreamingSource | null) => void;
    /*
     * The merged list of configured platforms with fresh /live-status overlaid.
     * Computed once in CreatorSection so the icons row and this player render
     * from the same data.
     */
    availablePlatforms?: FeaturedStreamResponse[];
}

export const StreamPanel: React.FC<StreamPanelProps> = ({
    surfaceId, dropSlug, hubName, streamingSettings, selectedClip, onClearSelectedClip,
    activePlatform: activePlatformProp,
    setActiveSource: setActiveSourceProp,
    availablePlatforms: availablePlatformsProp,
}) => {
    /*
     * availablePlatforms is owned by CreatorSection (so the compact source
     * picker in the tab nav and this player share state). Fall back to an
     * empty list when StreamPanel is rendered standalone — Drop pages without
     * any configured streams just don't have a player.
     */
    const availablePlatforms: FeaturedStreamResponse[] = availablePlatformsProp ?? [];

    /*
     * Live-status query kept here ONLY for `isLoading` (FOUC gating below).
     * React Query dedupes — CreatorSection already calls the same hook, so
     * no second network request fires. Source-aware: Drop routes through
     * the Drop endpoint, Hub routes through the Hub endpoint.
     */
    const dropLiveStatusQuery = useGetDropLiveStatus(dropSlug);
    const liveStatusQuery = dropLiveStatusQuery;

    const liveStreams = useMemo(() => availablePlatforms.filter(s => s.isLive), [availablePlatforms]);
    const primaryLive = liveStreams[0] ?? null;

    const setActiveSource = (platform: StreamingSource | null) => {
        if (setActiveSourceProp) setActiveSourceProp(platform);
    };
    /*
     * effectiveStream is the FeaturedStreamResponse the player should embed.
     * activePlatform arrives pre-resolved from CreatorSection, so the lookup
     * here is a direct map — no fallback chain in this component. Reusing
     * activePlatform (not the raw activeSource state) is what keeps this
     * value in lockstep with the SourceIcons highlight above the player.
     */
    const effectiveStream = useMemo(() => {
        if (activePlatformProp) {
            const found = availablePlatforms.find(s => s.platform === activePlatformProp);
            if (found) return found;
        }
        return availablePlatforms[0] ?? null;
    }, [activePlatformProp, availablePlatforms]);

    /* Hero fallback when offline = most-recent VOD/video from any platform. Source-aware. */
    const {hero: heroFallback, isLoading: isAnyVodLoading} = useHeroFallback(dropSlug);

    /*
     * `effectiveStreamLive` = "the user's selected source is live". When the
     * user explicitly selected a clip (from CLIPS tab), that takes precedence
     * over the live stream — matching Twitch / Kick / YouTube behavior where
     * clicking a past VOD swaps the player even while the creator is live,
     * surfaced with a "Return to live" affordance.
     */
    const effectiveStreamLive = !!effectiveStream?.isLive;
    const isShowingClip = !!selectedClip && selectedClip.source !== "TIKTOK";
    const isLive = effectiveStreamLive && !isShowingClip;

    /* "Return to live" affordance shows when a clip is selected AND any source is live */
    const liveReturnTarget = isShowingClip ? primaryLive : null;

    const posterUrl = isLive
        ? undefined
        : selectedClip?.thumbnailUrl ?? heroFallback?.thumbnailUrl;
    const posterTitle = isLive
        ? hubName ?? "Creator"
        : selectedClip?.title ?? heroFallback?.title ?? "Featured";
    /*
     * The live stream's platform and the return-to-live target's, each narrowed
     * ONCE to the three-platform UI vocabulary.
     *
     * Every read of these below used to be `as StreamingSource` on an optional
     * four-valued field. See `asStreamingSource` for what that cost; the rule
     * here is that no affordance which has to NAME a platform renders when
     * there is not exactly one.
     */
    const effectiveSource = asStreamingSource(effectiveStream?.platform);
    const liveReturnSource = asStreamingSource(liveReturnTarget?.platform);

    /*
     * The platform named on the poster banner.
     *
     * No fake TWITCH: this label names the platform a viewer is watching on,
     * and inventing one for a stream that has none tells them something untrue.
     * `undefined` rather than `"" as StreamingSource` — the empty string was
     * typed as a platform it is not, and rendered as a dangling separator
     * ("LIVE NOW · ") rather than as the absence it actually is.
     */
    /*
     * `| "TIKTOK"` because a selected CLIP can be one, and the banner names
     * the clip's source. `platformLabel` covers it.
     */
    const posterSource: StreamingSource | "TIKTOK" | undefined = isLive
        ? effectiveSource
        : isShowingClip
            ? selectedClip!.source
            : heroFallback?.source ?? "TWITCH";
    const posterSourceSuffix = posterSource ? ` · ${platformLabel(posterSource)}` : "";

    /*
     * Whether the current playback was started by a deliberate user action vs an
     * auto-mount on load. Feeds StreamInlinePlayer's `viewerInitiated` so
     * watch-time only accrues on real intent — the muted auto-mount on "went
     * live" does NOT count until the user actually engages with the player.
     */
    const [userInitiatedPlay, setUserInitiatedPlay] = useState(false);

    /*
     * The live stream's platform, IF the inline player can embed it.
     *
     * `InlinePlatform` is deliberately narrower than the API's
     * `StreamingPlatform`: it is the set the player has an embed for, and
     * `MULTI` is not one of them — there is no single channel to point an
     * iframe at. A MULTI stream therefore has no inline source, exactly like a
     * stream with no platform at all, and the panel falls through to its poster
     * state. Silently embedding "the first one" would play a channel the
     * operator never chose.
     */
    const embeddablePlatform: InlinePlatform | undefined =
        effectiveStream?.platform === "TWITCH" ||
        effectiveStream?.platform === "YOUTUBE" ||
        effectiveStream?.platform === "KICK"
            ? effectiveStream.platform
            : undefined;

    /* Build the InlinePlayerSource for whichever path the user clicks. */
    const inlineSource = useMemo(() => buildInlineSource({
        isShowingClip, selectedClip, effectiveStreamLive, effectiveStream,
        embeddablePlatform, heroFallback, availablePlatforms, hubName,
        userInitiatedPlay, surfaceId,
    }), [isShowingClip, selectedClip, effectiveStreamLive, effectiveStream, embeddablePlatform, heroFallback, hubName, availablePlatforms, userInitiatedPlay, surfaceId]);

    /*
     * `isPlaying` toggles the panel between poster and inline player.
     * Auto-start playback when:
     *   - selectedClip changes — explicit user intent
     *   - source is LIVE — viewer landed expecting to watch; force-click is friction
     * The inlineSource sets muted=true for the auto-mount cases so browser
     * autoplay policies don't block. The user can unmute via the player chrome.
     */
    const [isPlaying, setIsPlaying] = useState(false);
    /* source switch — drop back to the poster so the user re-confirms play */
    React.useEffect(() => {
        setIsPlaying(false);
        setUserInitiatedPlay(false);
    }, [activePlatformProp]);
    /* explicit clip selection — auto-start (deliberate: the user picked the clip) */
    React.useEffect(() => {
        if (selectedClip) {
            setIsPlaying(true);
            setUserInitiatedPlay(true);
        }
    }, [selectedClip?.id]);
    /* creator went live — auto-start, but NOT user-initiated (muted autoplay) */
    React.useEffect(() => {
        if (isLive) setIsPlaying(true);
    }, [isLive]);

    const handlePlay = () => {
        if (inlineSource) {
            setIsPlaying(true);
            setUserInitiatedPlay(true);
        }
    };

    const handleReturnToLive = () => {
        if (onClearSelectedClip) onClearSelectedClip();
        /*
         * Reset isPlaying explicitly — the [activeSource] effect bails out
         * when setActiveSource writes the same value React already holds, so
         * relying on it leaves the clip iframe mounted under a stale state.
         */
        setIsPlaying(false);
        /* Snap the source toggle to the live platform too so the player follows */
        if (liveReturnSource) setActiveSource(liveReturnSource);
    };

    /*
     * Right-rail content stacking (per spec):
     *   Active Challenge — ALWAYS rendered (real data when sponsor activates,
     *                       designed placeholder otherwise; the structure IS
     *                       the design)
     *   Chat            — BELOW the challenge, ONLY when source is live
     */
    const showChat = isLive && effectiveStream;

    /*
     * Active engagement (sponsor challenge) has no real data source yet.
     * Dev-only: reserve the offline right-rail column so ActiveChallengePanel
     * can render its scavenger-hunt placeholder on dev / staging / Mirror5 for
     * demos. Production stays empty (player goes full-width offline) until the
     * feed lands — flip to a real derived boolean (e.g. `!!activeChallenge`).
     */
    const hasEngagement = import.meta.env.VITE_ENVIRONMENT !== "production";

    /*
     * FOUC prevention. Two race conditions to cover:
     *   1. Live-status async — defaults to "not live", flashes offline poster
     *      over a stream that IS live until /live-status resolves.
     *   2. VOD queries async — when not live, the panel uses the most-recent
     *      VOD as the hero. Until twitch/kick/youtube VOD queries return,
     *      posterUrl is undefined → we render the empty fallback (stray play
     *      icon, no thumbnail) for a beat before content arrives.
     * Render the neutral skeleton while either is unresolved AND we have no
     * concrete content to show (clip selection bypasses both gates).
     */
    const isLiveStatusLoading = liveStatusQuery.isLoading && !liveStatusQuery.data;
    const isHeroLoading = !isLive && !selectedClip && !heroFallback && isAnyVodLoading;

    /*
     * "Nothing to show" terminal state — every relevant query has settled
     * (no longer loading), the creator isn't live, the user hasn't selected
     * a clip, and no VOD/video exists to use as a hero. Without this branch
     * the panel renders a disabled play button over an empty radial-gradient
     * bg with a meaningless "STREAM" banner — the exact "funky" state the
     * user flagged. Render a designed empty state instead.
     */
    const hasNoPlayableContent = !isLive &&
        !selectedClip &&
        !heroFallback &&
        !isAnyVodLoading &&
        !isLiveStatusLoading;

    /*
     * Fullscreen-with-chat. The Twitch/YouTube/Kick embeds each ship their own
     * fullscreen button, but it fullscreens ONLY the platform iframe — our
     * side-by-side ChatPanel (a separate cross-origin iframe) is lost. Instead
     * we fullscreen the whole player+chat ROW via the Fullscreen API so chat
     * stays alongside the video. Only surfaced when live (chat only renders
     * live). `fullscreenchange` keeps the button icon in sync with the native
     * Escape key exit.
     */
    const fullscreen = useFullscreenRow();

    /*
     * Watch-time tracking now lives inside StreamInlinePlayer (the single player
     * component every surface renders), keyed off real player play-state via the
     * accessible engagement model. StreamPanel no longer mount-tracks here — that
     * would double-count against the player's own session. surfaceId flows into the
     * player via inlineSource so the session still carries hub context.
     */

    /*
     * "Open on platform" link for the live stream, surfaced in the LIVE info bar
     * above the video instead of as an overlay over it — the overlay would sit
     * over the gameplay the viewer came to watch. Reuses the same URL builder
     * StreamInlinePlayer uses for its offline escape hatch (which is now
     * suppressed while live — see StreamInlinePlayer).
     */
    const livePopoutUrl = isLive && inlineSource ? getStreamExternalUrl(inlineSource) : undefined;

    return (
        <section className="hcs-stream-panel">
            {/* Source picker is in CreatorSection's tab nav row — see SourceIcons */}

            {/*
              * "Return to live" affordance — surfaces when the user is watching
              * a clip while the creator is also live, matching Twitch / Kick /
              * YouTube precedent.
              */}
            {liveReturnTarget &&
                <button type="button" className="hcs-live-return" onClick={handleReturnToLive}>
                    <FaCircle className="hcs-live-return__dot" />
                    {/* The platform phrase, only when there is one platform to
                        name — the button itself still offers the return. */}
                    <span>{hubName ?? "Creator"} is LIVE{liveReturnSource ? ` on ${platformLabel(liveReturnSource)}` : ""}</span>
                    <span className="hcs-live-return__cta">Return to live stream →</span>
                </button>
            }

            {/*
              * Player + chat layout. When LIVE, the player shares its row with
              * the platform-native chat iframe (270px right column). When
              * offline / playing a clip, the player goes full-width — chat is
              * suppressed because there's no live chat to render.
              *
              * Active engagement (sponsor challenge): on NON-production envs
              * `hasEngagement` is true and ActiveChallengePanel renders its
              * placeholder card in the right column (reserved via the
              * `--with-challenge` modifier). In production `hasEngagement` is
              * false, the panel isn't mounted, and the player takes the full
              * width when offline. Flip `hasEngagement` to a derived boolean
              * (e.g. `!!activeChallenge`) once the live engagement feed lands.
              */}
            <div ref={fullscreen.ref} className={`hcs-player-row${showChat ? " hcs-player-row--with-chat" : ""}${!isLive && hasEngagement ? " hcs-player-row--with-challenge" : ""}`}>
                <StreamPanelColumn
                    isLive={isLive}
                    isShowingClip={isShowingClip}
                    hasStream={!!effectiveStream}
                    effectiveSource={effectiveSource}
                    livePopoutUrl={livePopoutUrl}
                    fullscreen={fullscreen}
                    isLoading={isLiveStatusLoading || isHeroLoading}
                    hasNoPlayableContent={hasNoPlayableContent}
                    isPlaying={isPlaying}
                    inlineSource={inlineSource}
                    onPlay={handlePlay}
                    posterUrl={posterUrl}
                    posterTitle={posterTitle}
                    posterSourceSuffix={posterSourceSuffix}
                    heroFallback={heroFallback}
                    hubName={hubName}
                />
                {/* `effectiveSource`, not `effectiveStream` — chat is a
                    platform-native iframe, so with no single platform there is
                    no chat to embed. */}
                {showChat && effectiveStream && effectiveSource &&
                    <ChatPanel
                        platform={effectiveSource}
                        twitchChannelId={effectiveStream.twitchChannelId}
                        kickChannelSlug={effectiveStream.kickChannelSlug}
                        youtubeLiveId={effectiveStream.youtubeLiveId}
                    />
                }
                {/*
                  * Active Challenge in right rail ONLY when NOT live — when
                  * the creator is live, the player + chat take the row, and
                  * the challenge panel moves down to its own full-width
                  * section above SponsorChallengesRow (rendered by the
                  * parent HubCreatorSection — see live-state-driven render
                  * there). This keeps the live experience focused on
                  * watching, not on the placeholder card.
                  */}
                {!isLive && hasEngagement && <ActiveChallengePanel hubName={hubName} />}
            </div>
        </section>
    );
};
